import { useCallback, useEffect } from 'react';

import {
  IcicleChart as ReplotsIcicleChart,
  useChartTheme,
} from '@revelio/replots';

import { DataProviderQueryStatus } from '../../DataProvider/types';
import { ChartFallback } from '../ChartFallback';
import { useDrilldownHeaderBreadcrumb } from '../ChartHeaderBreadcrumb';
import { ChartLoader } from '../ChartLoader';
import { useChartMenuContext } from '../chart-menu/ChartMenuContext';
import { useChartLoadingStatus } from '../chart-menu/useChartLoadingStatus';
import {
  compositionDrilldownOtherColor,
  type DrilldownEntry,
  getDrilldownColor,
} from '../utils/composition-drilldown';
import styles from './IcicleChart.module.css';
import type { IcicleData, IcicleDrillStep } from './icicle-data';
import { formatIcicleShare } from './icicle-format';

export const IcicleContent = ({
  data,
  status,
  drilldownStack,
  onDrill,
  onNavigate,
  sizeTransform,
}: {
  data: IcicleData | undefined;
  status: DataProviderQueryStatus;
  drilldownStack: DrilldownEntry[];
  onDrill: (path: readonly IcicleDrillStep[], color: string) => void;
  onNavigate: (targetDepth: number) => void;
  sizeTransform: 'none' | 'sqrt' | undefined;
}) => {
  useChartLoadingStatus(status);
  const icicle = data?.icicle;
  const exportData = data?.exportData ?? null;
  const totalLabel =
    icicle !== undefined && icicle.nodes.length > 0 && drilldownStack.length > 0
      ? formatIcicleShare(1)
      : null;

  useDrilldownHeaderBreadcrumb({
    stack: drilldownStack,
    totalLabel,
    onNavigate,
  });

  const { setDataProviderSet } = useChartMenuContext();
  useEffect(() => {
    if (exportData !== null) setDataProviderSet(exportData);
  }, [exportData, setDataProviderSet]);

  const { theme } = useChartTheme();
  const otherColor = compositionDrilldownOtherColor(theme);
  const drillColor = getDrilldownColor(drilldownStack);

  const isNodeClickable = useCallback(
    (node: { id: string }) => {
      const drillPath = icicle?.meta.get(node.id)?.drillPath;
      return drillPath !== undefined && drillPath !== null;
    },
    [icicle]
  );

  const handleNodeClick = useCallback(
    (node: { id: string; color: string }) => {
      const drillPath = icicle?.meta.get(node.id)?.drillPath;
      if (drillPath === undefined || drillPath === null) return;
      onDrill(drillPath, node.color);
    },
    [icicle, onDrill]
  );

  const tooltipContent = useCallback(
    (node: { id: string; name: string; value: number | undefined }) => {
      const share = icicle?.meta.get(node.id)?.total ?? node.value ?? 0;
      return { title: node.name, valueText: formatIcicleShare(share) };
    },
    [icicle]
  );

  const isLoading =
    status === DataProviderQueryStatus.Fetching ||
    status === DataProviderQueryStatus.Idle;

  return (
    <>
      {isLoading ? (
        <div className={styles.loading}>
          <ChartLoader />
        </div>
      ) : (
        <ChartFallback status={status}>
          <div className={styles.container}>
            <ReplotsIcicleChart
              data={icicle?.nodes ?? []}
              otherColor={otherColor}
              fillColor={drillColor}
              onNodeClick={handleNodeClick}
              isNodeClickable={isNodeClickable}
              tooltipContent={tooltipContent}
              sizeTransform={sizeTransform}
            />
          </div>
        </ChartFallback>
      )}
    </>
  );
};
